import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../redux/store';
import { Tool } from '../../../../@types/IA.type';
import { monthNames } from '../../../../shared/constants/dates';
import { fetchPlatformsDeclarationsAndDetail } from '../../../../services/taxes.service';
import { fetchDeclarationsByYear } from '../../../../services/taxes.service';
import { CircleNotch, DownloadSimple, Info } from '@phosphor-icons/react';

const DeclarationReceiptTool = ({
	tool,
	userIsPlatec,
	userProfile,
}: {
	tool: Tool;
	userIsPlatec?: boolean;
	userProfile: any;
}) => {
	const [declaration, setDeclaration] = useState<any>(null);
	const { status: taxpayer } = useSelector(
		(state: RootState) => state.satLinkStatus,
	);
	const isLinked = taxpayer?.status === 'ACTIVE';

	const [isLoading, setIsLoading] = useState({
		status: false,
		declaration: false,
		annual: false,
	});

	const { args } = tool;

	const getPlatformsDeclarationsAndDetail = async () => {
		try {
			setIsLoading({ ...isLoading, declaration: true });
			const response = await fetchPlatformsDeclarationsAndDetail({
				month: args.month,
				year: args.year,
			});
			const lastDeclaration = response?.sort((a: any, b: any) => {
				return (
					new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
				);
			})?.[0];
			setDeclaration(lastDeclaration);
		} catch (error) {
			throw error;
		} finally {
			setIsLoading({ ...isLoading, declaration: false });
		}
	};

	const getAnnualDeclaration = async () => {
		try {
			setIsLoading({ ...isLoading, annual: true });
			const response = await fetchDeclarationsByYear(
				args.year,
				userProfile?.id!,
			);
			setDeclaration(response);
		} catch (error) {
			throw error;
		} finally {
			setIsLoading({ ...isLoading, annual: false });
		}
	};

	useEffect(() => {
		if (userProfile?.id && isLinked) {
			if (args.type === 'mensual' && userIsPlatec) {
				getPlatformsDeclarationsAndDetail();
			}
			if (args.type === 'anual') {
				getAnnualDeclaration();
			}
		}
	}, [
		userProfile?.id,
		isLinked,
		args.type,
		userIsPlatec,
		args.year,
		args.month,
	]);

	useEffect(() => {
		getPlatformsDeclarationsAndDetail();
	}, []);

	const declarationUrl =
		declaration?.detail_pdf || declaration?.presentation_pdf;

	const title = (() => {
		if (args.type.includes('mensual')) {
			return 'Acuse declaración';
		}
		if (args.type.includes('anual')) {
			return 'Acuse declaración Anual';
		}
		return '';
	})();

	const iconContainerStyles = (() => {
		if (isLoading.declaration) {
			return 'cursor-not-allowed bg-h-neutral-94';
		}
		if (declarationUrl) {
			return 'cursor-pointer bg-h-primary-95';
		}
		return 'cursor-not-allowed bg-h-neutral-94';
	})();

	const icon = (() => {
		if (isLoading.declaration) {
			return <CircleNotch className='animate-spin size-5 text-h-dark' />;
		}
		if (declarationUrl) {
			return (
				<DownloadSimple
					weight='bold'
					className='text-h-primary size-5'
				/>
			);
		}

		return (
			<Info
				weight='fill'
				className='opacity-30 size-5'
			/>
		);
	})();

	const description = (() => {
		if (isLoading.declaration) {
			return 'Buscando acuse de declaración';
		}
		if (declarationUrl) {
			return `Declaración ${declaration?.declaration_type?.toLowerCase()}`;
		}
		return 'No se encontró el acuse de declaración.';
	})();

	const month = monthNames[args.month - 1];

	return (
		<button
			className={`fade-in group text-left shadow bg-white font-light rounded-2xl p-1.5 w-fit flex gap-2 items-center pr-4  ${
				declarationUrl ? 'cursor-pointer' : 'cursor-not-allowed'
			}`}
			onClick={() => {
				if (declaration?.detail_pdf) {
					window.open(declaration?.detail_pdf, '_blank');
				}
			}}
		>
			<div
				className={`size-10 rounded-xl flex items-center justify-center ${iconContainerStyles}`}
			>
				{icon}
			</div>
			<p className='flex flex-col gap-1'>
				<span className='font-medium leading-4 truncate max-w-[70dvw]'>
					{title} {month} {args.year}
				</span>
				<span className='text-h-neutral-50 text-xs'>{description}.</span>
			</p>
		</button>
	);
};

export default DeclarationReceiptTool;
